// Harita sağlayıcısı seçimi. Google Maps Platform anahtarı verilmişse tüm harita gösterimi Google Maps JavaScript API ile
// yapılır (Places/Routes sonuçları yalnız Google haritasında gösterilebilir); anahtar yoksa ya da Google yüklenemezse MapLibre.
import { useSyncExternalStore } from 'react';

export type MapProvider = 'google' | 'maplibre';

export interface MapsConfig {
  /** zorla sağlayıcı; verilmezse anahtar varsa Google, yoksa MapLibre */
  provider?: MapProvider;
  /** Maps JavaScript API anahtarı (web: referrer kısıtlı) */
  googleApiKey?: string | null;
}

interface State {
  provider?: MapProvider;
  googleApiKey: string | null;
  googleFailed: boolean;
}

let state: State = { googleApiKey: null, googleFailed: false };
const listeners = new Set<() => void>();
let warned = false;

function emit() {
  listeners.forEach((l) => l());
}

function normalizeKey(key: string | null | undefined): string | null {
  const k = key?.trim();
  return k ? k : null;
}

/** Uygulama girişinde bir kez çağrılır (ör. `configureMaps({ googleApiKey: import.meta.env.VITE_GOOGLE_MAPS_KEY || null })`). */
export function configureMaps(next: MapsConfig): void {
  state = {
    ...state,
    provider: 'provider' in next ? next.provider : state.provider,
    googleApiKey: 'googleApiKey' in next ? normalizeKey(next.googleApiKey) : state.googleApiKey,
  };
  emit();
}

export function getMapsConfig(): Readonly<{ provider?: MapProvider; googleApiKey: string | null }> {
  return { provider: state.provider, googleApiKey: state.googleApiKey };
}

/** Saf seçim kuralı: MapLibre zorlanmışsa, anahtar yoksa ya da Google bu oturumda başarısız olduysa MapLibre. */
export function selectMapProvider(cfg: {
  provider?: MapProvider;
  googleApiKey?: string | null;
  googleFailed?: boolean;
}): MapProvider {
  if (cfg.provider === 'maplibre') return 'maplibre';
  if (!normalizeKey(cfg.googleApiKey)) return 'maplibre';
  if (cfg.googleFailed) return 'maplibre';
  return 'google';
}

export function getMapProvider(): MapProvider {
  return selectMapProvider(state);
}

/**
 * Google yüklenemedi (geçersiz anahtar, izin verilmeyen site, ağ): oturum boyunca MapLibre'ye dönülür.
 * Uyarı yalnız bir kez yazılır (console.warn — sayfalar hata günlüğü üretmez).
 */
export function reportGoogleMapsFailure(reason: unknown): void {
  if (!warned) {
    warned = true;
    const why = reason instanceof Error ? reason.message : String(reason);
    console.warn(`[SEROTA harita] Google Maps yüklenemedi (${why}); MapLibre haritasına dönüldü.`);
  }
  if (state.googleFailed) return;
  state = { ...state, googleFailed: true };
  emit();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Etkin sağlayıcı (yapılandırma ya da Google hatası değişince yeniden çizer). */
export function useMapProvider(): MapProvider {
  return useSyncExternalStore(subscribe, getMapProvider, () => 'maplibre' as MapProvider);
}

/** Yalnız testler için: yapılandırmayı ve hata durumunu sıfırlar. */
export function resetMapsConfigForTests(): void {
  state = { googleApiKey: null, googleFailed: false };
  warned = false;
  emit();
}
